import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { NAV, SITE } from "@/lib/site";
import { Menu, X, ArrowRight } from "lucide-react";

/**
 * Sticky header.
 *
 * Transparent while the reader is still inside the hero, then a frosted paper
 * bar once they have scrolled past it. Only the homepage has a hero to sit
 * over, so every other route starts solid.
 *
 * The mobile menu is a full-screen overlay: Escape closes it, body scroll is
 * locked while it is open, and focus returns to the trigger on close.
 */
export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(true);
  const { pathname } = useLocation();
  const overHero = pathname === "/";

  /* One IntersectionObserver on a sentinel beats a scroll listener: no work
     on any frame where the header state is not actually changing. */
  useEffect(() => {
    if (!overHero) {
      setScrolled(true);
      return undefined;
    }
    const sentinel = document.createElement("div");
    sentinel.setAttribute("aria-hidden", "true");
    sentinel.style.cssText = "position:absolute;top:0;left:0;width:1px;height:70svh;pointer-events:none";
    document.body.appendChild(sentinel);

    const io = new IntersectionObserver(
      ([entry]) => setScrolled(!entry.isIntersecting),
      { threshold: 0 }
    );
    io.observe(sentinel);
    setScrolled(false);

    return () => {
      io.disconnect();
      sentinel.remove();
    };
  }, [overHero]);

  /* Close on route change, so a link inside the overlay does not leave it up. */
  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <header
      className="site-header sticky top-0 z-50 border-b border-ink"
      data-scrolled={scrolled || open}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-8 h-[72px] flex items-center justify-between">
        <Link
          to="/"
          data-testid="wordmark-link"
          className="display text-2xl md:text-3xl leading-none tracking-tight inline-flex items-center min-h-[44px]"
          onClick={() => setOpen(false)}
        >
          {SITE.name}
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace("/", "")}`}
              className="ember-link text-[15px]"
            >
              {n.label}
            </NavLink>
          ))}
          <Link to="/book" className="pill pill-cobalt group" data-testid="nav-book-btn">
            Book a 15-min call
          </Link>
        </nav>

        <button
          className="md:hidden -mr-2 flex items-center justify-center min-w-[44px] min-h-[44px]"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-nav"
          data-testid="mobile-menu-btn"
        >
          {open ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
        </button>
      </div>

      {open && (
        <div
          id="mobile-nav"
          className="md:hidden fixed inset-x-0 top-[72px] bottom-0 bg-paper border-t border-ink overflow-y-auto"
          data-testid="mobile-nav"
        >
          <nav className="flex flex-col px-5 pt-6 pb-10">
            {NAV.map((n, i) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="mobile-menu__link display text-[34px] leading-[1.1] py-5 border-b border-ink/20"
                style={{ animationDelay: `${i * 55}ms` }}
                data-testid={`mnav-${n.to.replace("/", "")}`}
              >
                {n.label}
              </NavLink>
            ))}

            <Link
              to="/book"
              onClick={() => setOpen(false)}
              className="mobile-menu__link pill pill-cobalt mt-10 self-start group"
              style={{ animationDelay: `${NAV.length * 55}ms` }}
              data-testid="mnav-book"
            >
              Book a 15-minute call
              <ArrowRight size={16} className="cta-arrow" aria-hidden="true" />
            </Link>

            <a
              href={`mailto:${SITE.email}`}
              className="mobile-menu__link mt-8 text-[15px] text-ink/65 ember-link self-start"
              style={{ animationDelay: `${(NAV.length + 1) * 55}ms` }}
            >
              {SITE.email}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
